import { Component, OnInit, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { UserService } from '../../Services/user.service';
import { SweetAlertService } from 'src/app/Services/sweet-alert.service';
import { WoocommerceService } from 'src/app/Services/woocommerce.service';
import { Country, State } from 'src/app/models/woocommerce.model';
import { of } from 'rxjs';
import { catchError, startWith, switchMap, timeout } from 'rxjs/operators';
import { AuthService } from 'src/app/Services/auth.service';
import { DeviceDetectionService } from 'src/app/Services/device-detection.service';
import { PhoneVerificationService } from 'src/app/Services/phone-verification.service';

/**
 * Minimal bundled country list used when WooCommerce `/wc/v3/data/countries`
 * is slow or returns an error. India and the US ship with their full state
 * lists because they are by far the two most common SuperSee tenants; the
 * rest render as a flat country entry (user can type the state manually).
 * The real list from WooCommerce replaces this as soon as the HTTP settles.
 */
const BUNDLED_COUNTRY_FALLBACK: Country[] = [
    {
        code: 'IN',
        name: 'India',
        states: [
            { code: 'AN', name: 'Andaman and Nicobar Islands' },
            { code: 'AP', name: 'Andhra Pradesh' },
            { code: 'AR', name: 'Arunachal Pradesh' },
            { code: 'AS', name: 'Assam' },
            { code: 'BR', name: 'Bihar' },
            { code: 'CH', name: 'Chandigarh' },
            { code: 'CT', name: 'Chhattisgarh' },
            { code: 'DN', name: 'Dadra and Nagar Haveli' },
            { code: 'DD', name: 'Daman and Diu' },
            { code: 'DL', name: 'Delhi' },
            { code: 'GA', name: 'Goa' },
            { code: 'GJ', name: 'Gujarat' },
            { code: 'HR', name: 'Haryana' },
            { code: 'HP', name: 'Himachal Pradesh' },
            { code: 'JK', name: 'Jammu and Kashmir' },
            { code: 'JH', name: 'Jharkhand' },
            { code: 'KA', name: 'Karnataka' },
            { code: 'KL', name: 'Kerala' },
            { code: 'LA', name: 'Ladakh' },
            { code: 'LD', name: 'Lakshadweep' },
            { code: 'MP', name: 'Madhya Pradesh' },
            { code: 'MH', name: 'Maharashtra' },
            { code: 'MN', name: 'Manipur' },
            { code: 'ML', name: 'Meghalaya' },
            { code: 'MZ', name: 'Mizoram' },
            { code: 'NL', name: 'Nagaland' },
            { code: 'OR', name: 'Odisha' },
            { code: 'PY', name: 'Puducherry' },
            { code: 'PB', name: 'Punjab' },
            { code: 'RJ', name: 'Rajasthan' },
            { code: 'SK', name: 'Sikkim' },
            { code: 'TN', name: 'Tamil Nadu' },
            { code: 'TS', name: 'Telangana' },
            { code: 'TR', name: 'Tripura' },
            { code: 'UP', name: 'Uttar Pradesh' },
            { code: 'UT', name: 'Uttarakhand' },
            { code: 'WB', name: 'West Bengal' },
        ],
    },
    {
        code: 'US',
        name: 'United States',
        states: [
            { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' },
            { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
            { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
            { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
            { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' },
            { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
            { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
            { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' },
            { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
            { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
            { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' },
            { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
            { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
            { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' },
            { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
            { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
            { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' },
            { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
            { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
            { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' },
            { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
            { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
            { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' },
            { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
            { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
        ],
    },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'AU', name: 'Australia' },
    { code: 'BD', name: 'Bangladesh' },
    { code: 'CA', name: 'Canada' },
    { code: 'DE', name: 'Germany' },
    { code: 'ES', name: 'Spain' },
    { code: 'FR', name: 'France' },
    { code: 'IE', name: 'Ireland' },
    { code: 'IT', name: 'Italy' },
    { code: 'JP', name: 'Japan' },
    { code: 'KE', name: 'Kenya' },
    { code: 'LK', name: 'Sri Lanka' },
    { code: 'MY', name: 'Malaysia' },
    { code: 'NG', name: 'Nigeria' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'NP', name: 'Nepal' },
    { code: 'NZ', name: 'New Zealand' },
    { code: 'PH', name: 'Philippines' },
    { code: 'PK', name: 'Pakistan' },
    { code: 'QA', name: 'Qatar' },
    { code: 'SA', name: 'Saudi Arabia' },
    { code: 'SG', name: 'Singapore' },
    { code: 'TH', name: 'Thailand' },
    { code: 'TR', name: 'Turkey' },
    { code: 'VN', name: 'Vietnam' },
    { code: 'ZA', name: 'South Africa' },
];

@Component({
    selector: 'app-billing-profile-modal',
    templateUrl: './billing-profile-modal.component.html',
    styleUrls: ['./billing-profile-modal.component.css'],
    standalone: false
})
export class BillingProfileModalComponent implements OnInit {

    form: FormGroup;
    countries: Country[] = [];
    filteredCountries: Country[] = [];
    states: State[] = [];
    filteredStates: State[] = [];
    countryControl = new FormControl('');
    stateControl = new FormControl('');

    constructor(
        private fb: FormBuilder,
        public dialogRef: MatDialogRef<BillingProfileModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private userService: UserService,
        private sweetAlertService: SweetAlertService,
        private woocommerceService: WoocommerceService,
        private authService: AuthService,
        private dialog: MatDialog,
        private deviceDetectionService: DeviceDetectionService,
        private phoneVerificationService: PhoneVerificationService
    ) {
        this.form = this.fb.group({
            country: ['', [Validators.required]],
            state: ['', []],
            // phoneNumber: ['', [Validators.required, Validators.pattern('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\\s\\./0-9]*$')]],
            address1: ['', [Validators.required]],
            address2: [''],
            city: ['', [Validators.required]],
            zipCode: ['', [Validators.required]]
        });
    }

    ngOnInit(): void {
        // Render immediately with the bundled fallback so the Country dropdown
        // is never empty even if WooCommerce `/wc/v3/data/countries` is slow or
        // 400s on a cold tenant. When the real list arrives we swap it in and
        // re-filter without the user ever seeing a stuck form.
        this.countries = BUNDLED_COUNTRY_FALLBACK.slice();
        this.filteredCountries = this.countries;
        this.prefillFromDialogData();

        this.woocommerceService
            .getCountries()
            .pipe(
                timeout(8000),
                catchError(() => of([] as Country[]))
            )
            .subscribe((countries: Country[]) => {
                if (countries && countries.length) {
                    this.countries = countries;
                    this.filteredCountries = this.countries;
                    // Re-apply any prefilled country/state against the authoritative list.
                    this.prefillFromDialogData();
                }
                this._filterCountries(this.countryControl.value || '');
                this._filterStates(this.stateControl.value || '');
            });

        this.countryControl.valueChanges
            .pipe(startWith(''))
            .subscribe(value => this._filterCountries(value || ''));

        this.stateControl.valueChanges
            .pipe(startWith(''))
            .subscribe(value => this._filterStates(value || ''));
    }

    /** Prefill from WP meta and/or billing_address passed by Billing page. */
    private prefillFromDialogData(): void {
        const user = this.data || {};
        const meta = user.meta || {};
        const addr = user.billing_address || {};

        this.form.patchValue({
            address1: meta.billing_address_1 || addr.address_1 || '',
            address2: meta.billing_address_2 || addr.address_2 || '',
            city: meta.billing_city || addr.city || '',
            zipCode: meta.billing_postcode || addr.postcode || '',
        });

        const countryCode = (meta.billing_country || addr.country || '').toString().trim();
        const savedState = (meta.billing_state || addr.state || '').toString().trim();

        if (!countryCode) {
            return;
        }

        const match = this.countries.find(
            (c) => c.code.toLowerCase() === countryCode.toLowerCase()
        );
        const code = match?.code || countryCode;
        this.form.get('country')?.setValue(code);
        // countryControl stores the ISO code; displayWith (displayCountry) maps code → name.
        this.countryControl.setValue(code, { emitEvent: false });
        this.loadStatesAfterCountrySelect(code, savedState || null);
    }

    displayCountry(countryCode: string): string {
        const country = this.countries.find(c => c.code === countryCode);
        return country ? country.name : '';
    }

    displayState(stateCode: string): string {
        const state = this.states.find(s => s.code === stateCode);
        return state ? state.name : '';
    }

    private _filterCountries(value: string): void {
        const filterValue = value.toLowerCase();
        this.filteredCountries = this.countries.filter(country =>
            country.name.toLowerCase().includes(filterValue) ||
            country.code.toLowerCase().includes(filterValue)
        );
    }

    private _filterStates(value: string): void {
        const filterValue = value.toLowerCase();
        this.filteredStates = this.states.filter(state =>
            state.name.toLowerCase().includes(filterValue) ||
            state.code.toLowerCase().includes(filterValue)
        );
    }

    /**
     * Loads states for a country (embedded list or WooCommerce /countries/{code}/states)
     * and applies validators. Optionally restores a saved state code/name after load.
     */
    private loadStatesAfterCountrySelect(countryCode: string, restoreState: string | null): void {
        const selectedCountry = this.countries.find(c => c.code === countryCode);

        const applyStateList = (stateList: State[]) => {
            const list = stateList ?? [];
            if (list.length > 0) {
                this.states = list;
                this.filteredStates = list;
                this.form.get('state')?.setValidators([Validators.required]);
            } else {
                this.states = [];
                this.filteredStates = [];
                this.form.get('state')?.clearValidators();
            }
            this.form.get('state')?.updateValueAndValidity();

            if (restoreState) {
                const rs = restoreState.trim();
                const match = list.find(
                    s => s.code.toLowerCase() === rs.toLowerCase() || s.name.toLowerCase() === rs.toLowerCase()
                );
                if (match) {
                    this.form.get('state')?.setValue(match.code);
                    this.stateControl.setValue(match.code, { emitEvent: false });
                } else {
                    this.form.get('state')?.setValue(rs);
                    this.stateControl.setValue(rs, { emitEvent: false });
                }
            } else {
                this.stateControl.setValue('');
                this.form.get('state')?.setValue('');
            }
        };

        if (!selectedCountry) {
            applyStateList([]);
            return;
        }

        if (selectedCountry.states?.length) {
            applyStateList(selectedCountry.states);
            return;
        }

        this.states = [];
        this.filteredStates = [];
        this.form.get('state')?.setValidators([Validators.required]);
        this.form.get('state')?.setValue('');
        this.stateControl.setValue('', { emitEvent: false });
        this.form.get('state')?.updateValueAndValidity();

        this.woocommerceService.getStatesForCountry(selectedCountry.code).subscribe({
            next: (fetched: State[]) => {
                const list = fetched ?? [];
                selectedCountry.states = list;
                applyStateList(list);
            },
            error: () => applyStateList([])
        });
    }

    onCountrySelected(countryCode: string) {
        this.form.get('country')?.setValue(countryCode);
        this.countryControl.setValue(countryCode, { emitEvent: false });
        this.loadStatesAfterCountrySelect(countryCode, null);
    }

    onStateSelected(stateCode: string) {
        this.form.get('state')?.setValue(stateCode);
    }

    onCountryInputBlur(): void {
        const raw = (this.countryControl.value ?? '').toString().trim();
        if (!raw) {
            this.form.get('country')?.setValue('');
            this.form.get('country')?.markAsTouched();
            this.form.get('country')?.updateValueAndValidity();
            return;
        }
        const byCode = this.countries.find(c => c.code.toLowerCase() === raw.toLowerCase());
        const byName = this.countries.find(c => c.name.toLowerCase() === raw.toLowerCase());
        const matched = byCode || byName;
        if (matched) {
            this.countryControl.setValue(matched.code, { emitEvent: false });
            this.form.get('country')?.setValue(matched.code);
            this.loadStatesAfterCountrySelect(matched.code, null);
        } else {
            this.form.get('country')?.setValue('');
        }
        this.form.get('country')?.markAsTouched();
        this.form.get('country')?.updateValueAndValidity();
    }

    onStateInputBlur(): void {
        const raw = (this.stateControl.value ?? '').toString().trim();
        const stateFc = this.form.get('state');
        if (!this.states.length) {
            stateFc?.setValue(raw);
            stateFc?.markAsTouched();
            stateFc?.updateValueAndValidity();
            return;
        }
        if (!raw) {
            stateFc?.setValue('');
        } else {
            const byCode = this.states.find(s => s.code.toLowerCase() === raw.toLowerCase());
            const byName = this.states.find(s => s.name.toLowerCase() === raw.toLowerCase());
            const matched = byCode || byName;
            if (matched) {
                this.stateControl.setValue(matched.code, { emitEvent: false });
                stateFc?.setValue(matched.code);
            } else {
                stateFc?.setValue('');
            }
        }
        stateFc?.markAsTouched();
        stateFc?.updateValueAndValidity();
    }

    onSave(): void {
        if (this.form.valid) {
            const user = this.userService.getCurrentUserValue();
            if (!user) {
                this.sweetAlertService.showAlert('Error!', 'User not found. Please log in again.', 'error');
                return;
            }

            const performSave = () => {
                const billingAddress = {
                    first_name: user.name.split(' ')[0] || '',
                    last_name: user.name.split(' ')[1] || '',
                    company: user.meta.company_name || '',
                    address_1: this.form.value.address1,
                    address_2: this.form.value.address2,
                    city: this.form.value.city,
                    state: this.form.value.state,
                    postcode: this.form.value.zipCode,
                    country: this.form.value.country,
                    email: user.email,
                    // phone: this.form.value.phoneNumber,
                };

                const updatedData = {
                    billing: billingAddress
                };

                this.woocommerceService.updateCustomerBilling(user.id, updatedData).pipe(
                    switchMap(() => this.userService.fetchCurrentUser())
                ).subscribe((updatedUser) => {
                    this.authService.fetchUserDataAndSetup(updatedUser);
                    setTimeout(() => {
                        this.sweetAlertService.showAlert('Success!', 'Billing address saved successfully.', 'success');
                    }, 500);
                    this.dialogRef.close({ billing: billingAddress });
                }, () => {
                    this.sweetAlertService.showAlert('Error!', 'Failed to save billing address.', 'error');
                });
            };

            // Phone verification is now done before opening billing modal
            // Just proceed with saving
            performSave();
        }
    }

    onCancel(): void {
        this.dialogRef.close(false);
    }
} 